import { useEffect } from 'react'
import { lastPageShowsNothing } from '../ts-utils/pagedList'

export interface UseLoadPastHiddenPageArgs {
  /** Ids of the rows the last loaded page brought. */
  lastPageIds: readonly string[]
  /** Ids of the rows actually shown, after the caller's own hiding rules. */
  visibleIds: ReadonlySet<string>
  hasNextPage: boolean
  isFetchingNextPage: boolean
  /** Stop after a failed page, so a failing server isn't retried in a loop. */
  nextPageFailed: boolean
  fetchNextPage: () => void
}

/**
 * Keeps a paged list loading while its last page shows nothing.
 *
 * A list that hides some rows on the client (already asked, already rostered…)
 * can load a page that adds nothing to the screen. Scroll-driven loading then
 * stalls — the list didn't grow, so there's nothing to scroll and no end to
 * reach — and an empty list reads as "no results" while pages remain. This
 * loads the next page instead, until one shows something or the list ends.
 */
export const useLoadPastHiddenPage = ({
  lastPageIds,
  visibleIds,
  hasNextPage,
  isFetchingNextPage,
  nextPageFailed,
  fetchNextPage,
}: UseLoadPastHiddenPageArgs): void => {
  const shouldLoadNext =
    hasNextPage &&
    !isFetchingNextPage &&
    !nextPageFailed &&
    lastPageShowsNothing(lastPageIds, visibleIds)

  useEffect(() => {
    if (shouldLoadNext) {
      fetchNextPage()
    }
  }, [shouldLoadNext, fetchNextPage])
}

export default useLoadPastHiddenPage
